"use client";

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Clock } from "lucide-react";

function NumberInput({ id, value, onChange, min }) {
  return (
    <Input
      id={id}
      type="number"
      min={min}
      value={value}
      onChange={onChange}
      onFocus={(e) => e.target.select()}
    />
  );
}

export default function AssetFormDialog({
  open,
  onOpenChange,
  title,
  asset,
  setAsset,
  vendors,
  categories,
  conditions,
  statuses,
  loading,
  onSubmit,
  submitLabel,
  isEdit = false,
}) {
  const prefix = isEdit ? "edit" : "add";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">{title}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor={`${prefix}-asset-name`}>Asset Name*</Label>
            <Input
              id={`${prefix}-asset-name`}
              value={asset.asset_name}
              onChange={(e) => setAsset({ ...asset, asset_name: e.target.value })}
            />
          </div>

          <div>
            <Label htmlFor={`${prefix}-category`}>Category*</Label>
            <Select value={asset.category} onValueChange={(value) => setAsset({ ...asset, category: value })}>
              <SelectTrigger id={`${prefix}-category`}>
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((category) => (
                  <SelectItem key={category} value={category}>{category}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor={`${prefix}-vendor`}>Vendor</Label>
            <Select
              value={asset.vendor_id || "none"}
              onValueChange={(value) => {
                const selectedVendor = vendors.find((v) => v._id === value);
                setAsset({
                  ...asset,
                  vendor_id: value === "none" ? "" : value,
                  vendor_name: value === "none" ? "" : selectedVendor?.name || "",
                });
              }}
            >
              <SelectTrigger id={`${prefix}-vendor`}>
                <SelectValue placeholder="Select vendor" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Select vendor</SelectItem>
                {vendors.map((vendor) => (
                  <SelectItem key={vendor._id} value={vendor._id}>{vendor.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor={`${prefix}-model-number`}>Model Number</Label>
            <Input id={`${prefix}-model-number`} value={asset.model_number} onChange={(e) => setAsset({ ...asset, model_number: e.target.value })} />
          </div>

          <div>
            <Label htmlFor={`${prefix}-serial-number`}>Serial Number</Label>
            <Input id={`${prefix}-serial-number`} value={asset.serial_number} onChange={(e) => setAsset({ ...asset, serial_number: e.target.value })} />
          </div>

          <div>
            <Label htmlFor={`${prefix}-purchase-date`}>Purchase Date</Label>
            <Input id={`${prefix}-purchase-date`} type="date" value={asset.purchase_date} onChange={(e) => setAsset({ ...asset, purchase_date: e.target.value })} />
          </div>

          <div>
            <Label htmlFor={`${prefix}-purchase-cost`}>Purchase Cost (Rs)</Label>
            <NumberInput id={`${prefix}-purchase-cost`} value={asset.purchase_cost} onChange={(e) => setAsset({ ...asset, purchase_cost: Number.parseFloat(e.target.value) || 0 })} />
          </div>

          <div>
            <Label htmlFor={`${prefix}-warranty-period`}>Warranty Period</Label>
            <Input id={`${prefix}-warranty-period`} placeholder="e.g., 1 year" value={asset.warranty_period} onChange={(e) => setAsset({ ...asset, warranty_period: e.target.value })} />
          </div>

          <div>
            <Label htmlFor={`${prefix}-warranty-expiry`}>Warranty Expiry Date</Label>
            <Input id={`${prefix}-warranty-expiry`} type="date" value={asset.warranty_expiry_date} onChange={(e) => setAsset({ ...asset, warranty_expiry_date: e.target.value })} />
          </div>

          <div>
            <Label htmlFor={`${prefix}-assigned-to`}>Assigned To</Label>
            <Input id={`${prefix}-assigned-to`} value={asset.assigned_to} onChange={(e) => setAsset({ ...asset, assigned_to: e.target.value })} />
          </div>

          <div>
            <Label htmlFor={`${prefix}-location`}>Location</Label>
            <Input id={`${prefix}-location`} value={asset.location} onChange={(e) => setAsset({ ...asset, location: e.target.value })} />
          </div>

          <div className="col-span-1 sm:col-span-2 border rounded-lg p-4 bg-muted/30 space-y-3">
            <p className="text-sm font-medium">Unit counts</p>
            {!isEdit && (
              <p className="text-xs text-muted-foreground">
                One row can represent several identical units. In use = working units deployed; needs repair = broken or awaiting service.
              </p>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label htmlFor={`${prefix}-qty-total`}>Total units</Label>
                <NumberInput
                  id={`${prefix}-qty-total`}
                  min={1}
                  value={asset.quantity_total}
                  onChange={(e) => setAsset({ ...asset, quantity_total: Math.max(1, Number.parseInt(e.target.value, 10) || 1) })}
                />
              </div>
              <div>
                <Label htmlFor={`${prefix}-qty-in-service`}>In use</Label>
                <NumberInput
                  id={`${prefix}-qty-in-service`}
                  min={0}
                  value={asset.quantity_in_service}
                  onChange={(e) => setAsset({ ...asset, quantity_in_service: Math.max(0, Number.parseInt(e.target.value, 10) || 0) })}
                />
              </div>
              <div>
                <Label htmlFor={`${prefix}-qty-repair`}>Needs repair</Label>
                <NumberInput
                  id={`${prefix}-qty-repair`}
                  min={0}
                  value={asset.quantity_needs_repair}
                  onChange={(e) => setAsset({ ...asset, quantity_needs_repair: Math.max(0, Number.parseInt(e.target.value, 10) || 0) })}
                />
              </div>
            </div>
          </div>

          <div>
            <Label htmlFor={`${prefix}-depreciation-rate`}>Depreciation Rate (%)</Label>
            <NumberInput id={`${prefix}-depreciation-rate`} value={asset.depreciation_rate} onChange={(e) => setAsset({ ...asset, depreciation_rate: Number.parseFloat(e.target.value) || 0 })} />
          </div>

          <div>
            <Label htmlFor={`${prefix}-condition`}>Condition</Label>
            <Select value={asset.condition} onValueChange={(value) => setAsset({ ...asset, condition: value })}>
              <SelectTrigger id={`${prefix}-condition`}>
                <SelectValue placeholder="Select condition" />
              </SelectTrigger>
              <SelectContent>
                {conditions.map((condition) => (
                  <SelectItem key={condition} value={condition}>{condition}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {isEdit && (
            <div>
              <Label htmlFor={`${prefix}-status`}>Status</Label>
              <Select value={asset.status} onValueChange={(value) => setAsset({ ...asset, status: value })}>
                <SelectTrigger id={`${prefix}-status`}>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {statuses.map((status) => (
                    <SelectItem key={status} value={status}>{status}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className={isEdit ? "bg-[#0263cc] hover:bg-[#0263cc]/90" : "bg-[#0263cc] hover:bg-[#0263cc]/90"} onClick={onSubmit} disabled={loading}>
            {loading && <Clock className="mr-2 h-4 w-4 animate-spin" />}
            {submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
